import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Handles the Discord OAuth redirect. Discord bounces the user here with
 * ?code=&state= (success) or ?error=&error_description= (denied).
 *
 * Historical bug: on a token-exchange failure we used to silently restart
 * the OAuth flow (`loginWithDiscord()`), which produced an infinite
 * authorize -> callback -> authorize loop when the failure was permanent
 * (redirect_uri mismatch, revoked code, disabled Discord app, etc.).
 * We now show the actual error to the user and offer a manual retry.
 */
export default function DiscordCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, loginWithDiscord } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null); // { title, detail }

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");
    const oauthErrorDescription = params.get("error_description");

    // Strip code/state from URL immediately so a page refresh doesn't try
    // to re-use an already-consumed OAuth code (Discord + our backend
    // would both reject the second use).
    if (window.history?.replaceState) {
      window.history.replaceState({}, "", "/auth/discord/callback");
    }

    // The user hit "Cancel" on Discord's authorize screen.
    if (oauthError) {
      setError({
        title: "Sign-in cancelled",
        detail:
          oauthErrorDescription ||
          "You cancelled Discord authorization. Try again if this was a mistake.",
      });
      return;
    }

    if (!code || !state) {
      // No auth params at all — probably a stale bookmark or a refresh
      // after we already consumed the code. Send them home silently.
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/discord/callback", { code, state });
        setUser(data);
        toast.success(`Welcome back, ${data.name || "friend"}!`);
        navigate("/dashboard", { replace: true });
      } catch (err) {
        const status = err?.response?.status;
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          "Something went wrong on our end. Please try again in a minute.";
        // eslint-disable-next-line no-console
        console.error("Discord callback failed:", status, detail);

        // NEVER auto-restart the flow. That produced the historical
        // authorize -> callback -> authorize infinite loop. Just tell the
        // user what happened and let them retry manually.
        let title = "Sign-in failed";
        if (status === 403) title = "Sign-in expired";
        if (status === 400) title = "Discord rejected the sign-in";
        setError({ title, detail });
      }
    })();
  }, [params, navigate, setUser]);

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#05050A] px-4"
        data-testid="discord-callback-error"
      >
        <div className="w-full max-w-md rounded-2xl bg-[#0A0A0F] border border-red-500/30 p-8 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h1 className="font-heading text-2xl font-black text-white">
            {error.title}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">{error.detail}</p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              data-testid="callback-home"
              onClick={() => navigate("/", { replace: true })}
              className="h-10 px-5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold"
            >
              Back to homepage
            </button>
            <button
              data-testid="callback-retry"
              onClick={loginWithDiscord}
              className="h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-bold"
            >
              Try again
            </button>
          </div>
          <p className="mt-4 text-[10px] font-mono text-zinc-600">
            If this keeps happening, open a support ticket in our Discord.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#05050A]"
      data-testid="discord-callback-loading"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-2 border-blurple border-t-transparent rounded-full animate-spin" />
        <p className="text-zinc-400 font-mono text-sm">Signing in with Discord...</p>
      </div>
    </div>
  );
}
